// Added the Style 2 Ask carousel behavior, controls, and lifecycle handling - Lv Jianming - 2026-08-13 19:23
(() => {
  const elementName = 'delta-ask-style2-carousel';

  if (customElements.get(elementName)) return;

  class DeltaAskStyle2Carousel extends HTMLElement {
    connectedCallback() {
      if (this._deltaSwiper && !this._deltaSwiper.destroyed) return;
      if (typeof window.Swiper !== 'function') return;

      this._abortController = new AbortController();
      this._root = this.closest('.part-delta-ask--style-2');
      this._tabs = this._root?.querySelector('.extract-tabs') || null;
      this._overflowCue =
        this._root?.querySelector('.extract-tabs-next') || null;
      this._autoplayTimer = null;
      this._autoplayHoverPaused = false;
      this._autoplayFocusPaused = false;
      this._teleporting = false;
      this._prepareLoopSlides();

      this._tabs?.addEventListener('click', this._handleTabClick, {
        signal: this._abortController.signal,
      });
      this._tabs?.addEventListener('scroll', this._handleTabScroll, {
        passive: true,
        signal: this._abortController.signal,
      });
      window.addEventListener('resize', this._handleViewportResize, {
        signal: this._abortController.signal,
      });
      this._root?.addEventListener('mouseenter', this._handleAutoplayMouseEnter, {
        signal: this._abortController.signal,
      });
      this._root?.addEventListener('mouseleave', this._handleAutoplayMouseLeave, {
        signal: this._abortController.signal,
      });
      this._root?.addEventListener('focusin', this._handleAutoplayFocusIn, {
        signal: this._abortController.signal,
      });
      this._root?.addEventListener('focusout', this._handleAutoplayFocusOut, {
        signal: this._abortController.signal,
      });

      this._autoplayEnabled =
        this.dataset.autoplay === 'true' && !window.Shopify?.designMode;

      this._deltaSwiper = new window.Swiper(this, {
        slidesPerView: 'auto',
        spaceBetween: 24,
        centeredSlides: false,
        // Swiper's virtual loop can reorder the rail during a transition.
        // The explicit clones below keep both desktop peeks present while
        // allowing us to teleport only after the animation has completed.
        loop: false,
        initialSlide: this._cloneCount,
        watchOverflow: false,
        speed: this._getSpeed(),
        grabCursor: this.dataset.grabCursor === 'true',
        watchSlidesProgress: true,
        autoplay: false,
        navigation: {
          prevEl: this.querySelector('.swiper-button-prev'),
          nextEl: this.querySelector('.swiper-button-next'),
        },
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          type: 'progressbar',
        },
        breakpoints: this._getBreakpoints(),
        on: {
          init: (swiper) => {
            this._syncState(swiper);
            this._scheduleAutoplay();
          },
          slideChange: (swiper) => this._syncState(swiper),
          slideChangeTransitionEnd: (swiper) => {
            this._handleTransitionEnd(swiper);
            this._syncState(swiper);
            this._scheduleAutoplay();
          },
        },
      });

      // Swiper can fire `init` before the constructor assignment above is
      // complete, so schedule the first autoplay tick once the instance is
      // available on the element.
      this._scheduleAutoplay();
      this._syncOverflowCue();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._abortController = null;
      this._clearAutoplay();
      this._pauseAllMedia();

      if (this._deltaSwiper && !this._deltaSwiper.destroyed) {
        this._deltaSwiper.destroy(true, true);
      }

      this._deltaSwiper = null;
      this._removeClones();
      this._root = null;
      this._tabs = null;
      this._overflowCue = null;
      this._autoplayHoverPaused = false;
      this._autoplayFocusPaused = false;
      this._realSlideCount = 0;
      this._cloneCount = 0;
    }

    _getBreakpoints() {
      if (!this.dataset.customBreakpoints) return undefined;

      try {
        return JSON.parse(this.dataset.customBreakpoints);
      } catch (_error) {
        return undefined;
      }
    }

    _getSpeed() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        return 0;
      }

      return Number(this.dataset.speed) || 400;
    }

    _prepareLoopSlides() {
      this._removeClones();

      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper) {
        this._realSlideCount = 0;
        this._cloneCount = 0;
        return;
      }

      const realSlides = Array.from(wrapper.children).filter(
        (slide) => !slide.classList.contains('cm-clone'),
      );
      this._realSlideCount = realSlides.length;
      this._cloneCount = Math.min(2, this._realSlideCount);

      if (this._realSlideCount < 2) return;

      const prependClones = realSlides.slice(-this._cloneCount).map((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('cm-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        return clone;
      });
      const appendClones = realSlides.slice(0, this._cloneCount).map((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('cm-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        return clone;
      });

      // Prepending reverses insertion order, so insert the source clones from
      // nearest-to-first to nearest-to-last to preserve the real rail order.
      prependClones.reverse().forEach((clone) => wrapper.prepend(clone));
      appendClones.forEach((clone) => wrapper.append(clone));
    }

    _removeClones() {
      this.querySelectorAll('.swiper-wrapper > .cm-clone').forEach((clone) => {
        clone.remove();
      });
    }

    _getRealIndex(swiper) {
      if (!this._realSlideCount) return 0;

      const relativeIndex = (Number(swiper.activeIndex) || 0) - this._cloneCount;
      return (
        ((relativeIndex % this._realSlideCount) + this._realSlideCount) %
        this._realSlideCount
      );
    }

    _handleTabClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const tab = event.target.closest('[data-slide-index]');
      if (!tab || !this._tabs?.contains(tab)) return;

      const slideIndex = Number(tab.dataset.slideIndex);
      const swiper = this._deltaSwiper;
      if (
        !Number.isInteger(slideIndex) ||
        !swiper ||
        swiper.destroyed ||
        slideIndex < 0 ||
        slideIndex >= this._realSlideCount
      ) {
        return;
      }

      const currentIndex = this._getRealIndex(swiper);
      if (slideIndex === currentIndex) return;

      const forwardDistance =
        (slideIndex - currentIndex + this._realSlideCount) % this._realSlideCount;
      const backwardDistance = forwardDistance - this._realSlideCount;
      const distance =
        Math.abs(backwardDistance) < Math.abs(forwardDistance)
          ? backwardDistance
          : forwardDistance;

      this._clearAutoplay();
      const lastSlideIndex =
        this._cloneCount + this._realSlideCount + this._cloneCount - 1;
      const targetIndex = swiper.activeIndex + distance;
      swiper.slideTo(
        targetIndex >= 0 && targetIndex <= lastSlideIndex
          ? targetIndex
          : this._cloneCount + slideIndex,
        this._getSpeed(),
      );
    };

    _handleTabScroll = () => {
      this._syncOverflowCue();
    };

    _handleViewportResize = () => {
      this._syncOverflowCue();
    };

    _handleAutoplayMouseEnter = () => {
      this._autoplayHoverPaused = true;
      this._updateAutoplayPauseState();
    };

    _handleAutoplayMouseLeave = () => {
      this._autoplayHoverPaused = false;
      this._updateAutoplayPauseState();
    };

    _handleAutoplayFocusIn = () => {
      this._autoplayFocusPaused = true;
      this._updateAutoplayPauseState();
    };

    _handleAutoplayFocusOut = (event) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && this._root?.contains(nextTarget)) return;

      this._autoplayFocusPaused = false;
      this._updateAutoplayPauseState();
    };

    _handleTransitionEnd(swiper) {
      if (
        this._teleporting ||
        swiper.destroyed ||
        !this._realSlideCount ||
        !this._cloneCount
      ) {
        return;
      }

      const firstRealIndex = this._cloneCount;
      const firstAppendIndex = firstRealIndex + this._realSlideCount;
      let targetIndex = null;

      if (swiper.activeIndex < firstRealIndex) {
        targetIndex = swiper.activeIndex + this._realSlideCount;
      } else if (swiper.activeIndex >= firstAppendIndex) {
        targetIndex = swiper.activeIndex - this._realSlideCount;
      }

      if (targetIndex === null) return;

      this._teleporting = true;
      swiper.slideTo(targetIndex, 0, false);
      this._teleporting = false;
    }

    _scheduleAutoplay() {
      this._clearAutoplay();

      if (
        !this._autoplayEnabled ||
        this._autoplayHoverPaused ||
        this._autoplayFocusPaused ||
        !this._deltaSwiper ||
        this._realSlideCount < 2
      ) {
        return;
      }

      const delay = (Number(this.dataset.autoplaySpeed) || 5) * 1000;
      this._autoplayTimer = window.setTimeout(() => {
        const swiper = this._deltaSwiper;
        if (!swiper || swiper.destroyed) return;

        swiper.slideTo(swiper.activeIndex + 1, this._getSpeed());
      }, delay);
    }

    _updateAutoplayPauseState() {
      if (this._autoplayHoverPaused || this._autoplayFocusPaused) {
        this._clearAutoplay();
        return;
      }

      this._scheduleAutoplay();
    }

    _clearAutoplay() {
      if (this._autoplayTimer === null) return;

      window.clearTimeout(this._autoplayTimer);
      this._autoplayTimer = null;
    }

    _syncState(swiper) {
      const realIndex = this._getRealIndex(swiper);

      this._root?.querySelectorAll('[data-slide-index]').forEach((item) => {
        const isActive = Number(item.dataset.slideIndex) === realIndex;

        if (item.classList.contains('tab-item')) {
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
          if (isActive) this._revealTab(item);
        }

        if (item.classList.contains('delta-ask-s2-caption')) {
          item.hidden = !isActive;
        }
      });

      this._syncProgress(realIndex);
      this._syncMedia(swiper);
    }

    _syncProgress(realIndex) {
      const fill = this.querySelector('.swiper-pagination-progressbar-fill');
      if (!fill) return;

      const progress =
        this._realSlideCount > 1
          ? (realIndex + 1) / this._realSlideCount
          : 1;
      fill.style.transform = `scaleX(${progress}) translate3d(0, 0, 0)`;
    }

    _revealTab(tab) {
      if (!this._tabs || window.innerWidth >= 768) return;

      const tabs = Array.from(this._tabs.querySelectorAll('.tab-item'));
      const isLastTab = tab === tabs[tabs.length - 1];
      const overlayWidth = isLastTab
        ? 0
        : Number.parseFloat(
            window.getComputedStyle(this._tabs).scrollPaddingRight
          ) || 0;
      const visibleStart = this._tabs.scrollLeft;
      const visibleEnd = visibleStart + this._tabs.clientWidth - overlayWidth;
      const tabStart = tab.offsetLeft;
      const tabEnd = tabStart + tab.offsetWidth;

      if (tabStart < visibleStart) {
        this._tabs.scrollTo({ left: tabStart, behavior: 'auto' });
      } else if (tabEnd > visibleEnd) {
        this._tabs.scrollTo({
          left: tabEnd - this._tabs.clientWidth + overlayWidth,
          behavior: 'auto',
        });
      }

      if (this._overflowCue) this._overflowCue.hidden = isLastTab;
      window.requestAnimationFrame(() => this._syncOverflowCue());
    }

    _syncOverflowCue() {
      if (!this._tabs || !this._overflowCue) return;

      if (window.innerWidth >= 768) {
        this._overflowCue.hidden = false;
        return;
      }

      const hasOverflow = this._tabs.scrollWidth > this._tabs.clientWidth + 1;
      const isAtEnd =
        this._tabs.scrollLeft + this._tabs.clientWidth >=
        this._tabs.scrollWidth - 1;
      this._overflowCue.hidden = !hasOverflow || isAtEnd;
    }

    _syncMedia(swiper) {
      swiper.slides?.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;

        if (slide.classList.contains('swiper-slide-active')) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    _pauseAllMedia() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  }

  customElements.define(elementName, DeltaAskStyle2Carousel);
})();
// End added the Style 2 Ask carousel behavior, controls, and lifecycle handling - Lv Jianming - 2026-08-13 19:23
